import type { MoreProps, ChartId } from '@/types/commontypes';
import {
  calculateBarRange,
  calculateDaysBetweenPoints,
  calculatePercentChange,
  calculatePriceRange,
  formatSmallPrice,
} from '@/utils/duration';
import { calculateTotalVolume } from '@/utils/calculateVolumeBars';
import { formatDaysBetween, formatNumber } from '@/utils/combinedText';
import { clampYToChart } from '@/utils/useShapeActions';
import { chartOrder } from '@/utils/chartConfig';

/** computeMeasureMetrics의 반환 타입 */
export interface MeasureMetrics {
  rect: { x: number; y: number; width: number; height: number };
  end: { item: unknown; xValue: number; yValue: number };
  textPosition: { xValue: number; yValue: number };
  textLines: string[];
  barsRange: number;
  daysBetween: { days: number; hours: number; minutes: number };
  priceRange: number;
  totalPercentChange: number;
  totalVolume: string | number;
  arrowDirectionHorz: string | undefined;
  arrowDirectionVert: string | undefined;
  horizontalStartXY: [number, number];
  horizontalEndXY: [number, number];
  verticalStartXY: [number, number];
  verticalEndXY: [number, number];
}

/**
 * Measure 도형의 파생 메트릭(rect, 화살표 좌표, 텍스트 라인 등)을 계산한다.
 *
 * Measure.tsx의 handleRectCreate()에서 추출한 순수 함수.
 * 클래스 컴포넌트 렌더 시점에서 호출하여 파생값을 계산한다.
 */
export function computeMeasureMetrics(
  startPosition: { xValue: number; yValue: number },
  chartId: ChartId,
  shapesChartId: ChartId,
  timeFrame: string,
  moreProps: MoreProps,
): MeasureMetrics {
  const {
    mouseXY: [, mouseY],
    currentItem,
    chartConfig: { yScale: pointerYScale },
    xAccessor,
    xScale,
    chartConfigs,
  } = moreProps;

  const x = xAccessor(currentItem);

  // Get start chart Y scale
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const chartConfigsArr = (chartConfigs ?? []) as Array<{ id: string; yScale: (v: number) => number }>;
  const startChartConfig = chartConfigsArr.find(c => c.id === shapesChartId);
  const startYScale = startChartConfig?.yScale;

  // Determine final Y in domain coordinates
  let finalDomainY: number;

  if (chartId === shapesChartId) {
    // Same chart: use mouse
    finalDomainY = pointerYScale.invert(mouseY);
  } else {
    // Different chart: clamp Y to original chart
    finalDomainY = clampYToChart(chartId, shapesChartId, chartConfigs, chartOrder, startPosition.yValue);
  }

  // Convert to pixel coordinates in original chart
  const x1 = xScale(startPosition.xValue);
  const x2 = xScale(x);
  const y1 = startYScale ? startYScale(startPosition.yValue) : 0;
  const y2 = startYScale ? startYScale(finalDomainY) : 0;

  const width = Math.abs(x2 - x1);
  const height = Math.abs(y2 - y1);
  const rectX = Math.min(x1, x2);
  const rectY = Math.min(y1, y2);
  const rect = {
    x: rectX,
    y: rectY,
    width,
    height,
  };

  // Compute metrics
  const barsRange = calculateBarRange(moreProps, startPosition.xValue, x);
  const daysBetween = calculateDaysBetweenPoints(barsRange, timeFrame);
  const priceRange = calculatePriceRange(startPosition.yValue, finalDomainY);
  const totalPercentChange = calculatePercentChange(startPosition.yValue, finalDomainY);
  const totalVolume = calculateTotalVolume(moreProps, startPosition.xValue, x);

  const textLines = computeTextLines({
    priceRange,
    totalPercentChange,
    barsRange,
    daysBetween,
    totalVolume,
  });

  const verticalStartXY: [number, number] = [rectX + width / 2, rectY];
  const verticalEndXY: [number, number] = [rectX + width / 2, rectY + height];
  const horizontalStartXY: [number, number] = [rectX, rectY + height / 2];
  const horizontalEndXY: [number, number] = [rectX + width, rectY + height / 2];

  // Use DOMAIN values only
  const isDrawingRight = x >= startPosition.xValue;
  const isDrawingUp = finalDomainY > startPosition.yValue;

  // Horizontal arrow (left/right based on X movement)
  const horzDirection = isDrawingRight ? 'right' : 'left';

  // Vertical arrow (still left/right, but based on Y movement)
  const vertDirection = isDrawingUp ? 'left' : 'right';

  // Hide arrows when rectangle is small
  const arrowDirectionHorz = width > 60 ? horzDirection : undefined;
  const arrowDirectionVert = height > 50 ? vertDirection : undefined;

  const centerX = rectX + width / 2;
  const baseY = rectY + height + 25;
  const textPosition = { xValue: centerX, yValue: baseY };

  return {
    rect,
    end: { item: moreProps.currentItem, xValue: x, yValue: finalDomainY },
    textPosition,
    textLines,
    barsRange,
    daysBetween,
    priceRange,
    totalPercentChange,
    totalVolume,
    arrowDirectionHorz,
    arrowDirectionVert,
    horizontalStartXY,
    horizontalEndXY,
    verticalStartXY,
    verticalEndXY,
  };
}

/**
 * Measure 도형의 텍스트 라인을 생성한다.
 *
 * Measure 클래스의 static computeTextLines()에서 추출.
 */
export function computeTextLines(data: {
  priceRange?: number;
  totalPercentChange?: number;
  barsRange?: number;
  daysBetween?: { days?: number; hours?: number; minutes?: number };
  totalVolume?: string | number;
}): string[] {
  if (!data) return [];

  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const priceChange = formatNumber(parseFloat(formatSmallPrice(data.priceRange as number))) || 0;
  const percentChange = Number(data.totalPercentChange) || 0;

  const line1 = `${priceChange} (${percentChange}%)`;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const durationParts = formatDaysBetween(data.daysBetween as { days?: number; hours?: number; minutes?: number });
  const line2 = `${data.barsRange} bars${durationParts ? `, ${durationParts}` : ''}`;
  const line3 = data.totalVolume ? `Vol: ${data.totalVolume}` : '';
  return [line1, line2, line3];
}
